import { gsap } from "gsap";
import { Vector3, Euler } from "three";

// Animation configuration constants
const START_DELAY = 0; // Delay before reveal animation starts (seconds) - set to 0 for immediate start
const FLIP_DURATION = 0.8; // Duration to flip coin right side up (seconds)
const MOVE_DURATION = 3; // Duration to move coin to center and camera (seconds)
const SPIN_ROTATIONS = 1; // Number of full rotations during reveal animation
const TARGET_Z_OFFSET_FROM_CAMERA = -3.5; // Z offset from camera position (negative = closer to camera)
const END_Z_POSITION = -10; // Z position when coin reaches end of reveal animation
const FLIP_EASE = "power4.out"; // GSAP easing function
const POSITION_EASE = "power2.inOut"; // GSAP easing function
const SPIN_EASE_TYPE = "power2.inOut"; // Easing for the spinning rotation
const REVEAL_HOLD_DURATION = 2.5; // Duration to hold the reveal animation (seconds)
const EXIT_DURATION = 1; // Duration to exit the reveal animation (seconds)
const EXIT_EASE = "power2.inOut"; // GSAP easing function
const BLUR_FADE_IN_DURATION = 0.5; // Duration to fade in blur (seconds)
const BLUR_EASE = "power2.inOut"; // GSAP easing function for blur

class RevealAnimationManager {
  animationTimeline = null;
  isAnimating = false;
  animatedCoin = null;
  originalContainerPosition = new Vector3();
  originalContainerRotation = new Euler();
  originalMeshPosition = new Vector3();
  originalMeshRotation = new Euler();
  onComplete = null;
  onResultRevealed = null; // Callback when coin reaches center
  blurAmount = { value: 0 }; // Object to track blur amount for GSAP animation
  onBlurUpdate = null; // Callback to update blur when GSAP animates it
  prefersReducedMotion = false;

  constructor({ onComplete, onBlurUpdate, onResultRevealed, prefersReducedMotion = false }) {
    this.onComplete = onComplete;
    this.onBlurUpdate = onBlurUpdate;
    this.onResultRevealed = onResultRevealed;
    this.prefersReducedMotion = prefersReducedMotion;
  }

  /**
   * Start the reveal animation for the winning coin
   * @param {Object} params
   * @param {Coin} params.coin - The winning coin to animate
   * @param {Object} params.camera - The scene camera
   * @param {Object} params.scene - The Three.js scene
   * @param {Function} params.onComplete - Callback function to call when the animation is complete
   */
  startRevealAnimation({ coin, camera, scene }) {
    if (this.isAnimating) {
      this.stopAnimation();
    }

    this.isAnimating = true;
    this.animatedCoin = coin;

    // Store original transform values
    this.originalContainerPosition.copy(coin.container.position);
    this.originalContainerRotation.copy(coin.container.rotation);
    this.originalMeshPosition.copy(coin.mesh.position);
    this.originalMeshRotation.copy(coin.mesh.rotation);

    // Get current world position to calculate target
    const worldPosition = new Vector3();
    coin.container.getWorldPosition(worldPosition);

    // Calculate target Z position relative to current camera position
    // This ensures the coin appears centered in the viewport regardless of camera distance
    const currentCameraZ = camera.position.z;
    const targetZPosition = currentCameraZ + TARGET_Z_OFFSET_FROM_CAMERA;

    // Calculate durations based on reduced motion preference
    const blurDuration = this.prefersReducedMotion ? 0 : BLUR_FADE_IN_DURATION;
    const flipDuration = this.prefersReducedMotion ? 0 : FLIP_DURATION;
    const moveDuration = this.prefersReducedMotion ? 0 : MOVE_DURATION;
    const exitDuration = this.prefersReducedMotion ? 0 : EXIT_DURATION;
    const easeType = this.prefersReducedMotion ? "none" : undefined;

    // Calculate total animation duration for reduced motion hold time
    const totalAnimationDuration = FLIP_DURATION + MOVE_DURATION + REVEAL_HOLD_DURATION + EXIT_DURATION;
    const holdDuration = this.prefersReducedMotion ? totalAnimationDuration : REVEAL_HOLD_DURATION;

    // If reduced motion, set coin position, rotation, and blur instantly
    if (this.prefersReducedMotion) {
      coin.container.position.set(0, 0, targetZPosition);
      coin.container.rotation.set(Math.PI * 0.5, Math.PI * -0.5, 0);
      coin.mesh.position.set(0, 0, 0);
      this.blurAmount.value = 1;
      this.onBlurUpdate?.(1);
    }

    // Create a timeline for the reveal animation
    this.animationTimeline = gsap.timeline({
      onComplete: () => {
        this.onComplete?.();
      },
    });

    // Fade in blur at the start
    this.animationTimeline.to(
      this.blurAmount,
      {
        value: 1,
        duration: blurDuration,
        ease: easeType || BLUR_EASE,
        onUpdate: () => {
          this.onBlurUpdate?.(this.blurAmount.value);
        },
      },
      START_DELAY
    );

    // Flip coin right side up
    this.animationTimeline.to(
      coin.mesh.rotation,
      {
        y: Math.PI,
        x: 0,
        duration: flipDuration,
        ease: easeType || FLIP_EASE,
      },
      START_DELAY
    );

    // Move to center and camera while spinning
    this.animationTimeline.to(
      coin.container.position,
      {
        x: 0,
        y: 0,
        z: targetZPosition,
        duration: moveDuration,
        ease: easeType || POSITION_EASE,
        onComplete: () => {
          // Trigger toast when coin reaches center
          this.onResultRevealed?.();
        },
      },
      '>'
    );

    // Move mesh to center of container
    this.animationTimeline.to(
      coin.mesh.position,
      {
        x: 0,
        y: 0,
        z: 0,
        duration: moveDuration,
        ease: easeType || POSITION_EASE,
      },
     '<'
    );

    // Add slow spinning rotation during movement
    // Start from 0 (after flip) and rotate to targetRotationX
    const targetRotationX = SPIN_ROTATIONS * Math.PI * 2;
    this.animationTimeline.to(
      coin.mesh.rotation,
      {
        x: targetRotationX,
        duration: moveDuration,
        ease: easeType || SPIN_EASE_TYPE,
      },
      '<'
    );

    this.animationTimeline.to(
      coin.container.position,
      {
        z: END_Z_POSITION,
        duration: exitDuration,
        ease: easeType || EXIT_EASE,
      },
      `>${holdDuration}`
    );

    const targetExitRotationX = SPIN_ROTATIONS * Math.PI * 2 * 2;
    this.animationTimeline.to(
      coin.mesh.rotation,
      {
        x: targetExitRotationX,
        duration: exitDuration,
        ease: easeType || EXIT_EASE,
      },
      '<'
    );
  }

  /**
   * Stop the current animation and reset coin to original state
   */
  stopAnimation() {
    if (this.animationTimeline) {
      this.animationTimeline.kill();
      this.animationTimeline = null;
    }

    if (this.animatedCoin && this.isAnimating) {
      // Reset coin to original position/rotation
      this.animatedCoin.container.position.copy(this.originalContainerPosition);
      this.animatedCoin.container.rotation.copy(this.originalContainerRotation);
      this.animatedCoin.mesh.position.copy(this.originalMeshPosition);
      this.animatedCoin.mesh.rotation.copy(this.originalMeshRotation);
    }

    // Reset blur amount
    this.blurAmount.value = 0;
    this.onBlurUpdate?.(0);

    this.animatedCoin = null;
    this.isAnimating = false;
  }

  /**
   * Check if animation is currently running
   * @returns {boolean}
   */
  getIsAnimating() {
    return this.isAnimating;
  }

  /**
   * Get the coin currently being animated
   * @returns {Coin|null}
   */
  getAnimatedCoin() {
    return this.animatedCoin;
  }

  /**
   * Get the current blur amount
   * @returns {number}
   */
  getBlurAmount() {
    return this.blurAmount.value;
  }
}

export default RevealAnimationManager;

